import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { colors } from '../../../../../../colors';
import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../../../helpers/NumberFormatter';
import { Transaction } from '../../../../../Overview/types';
import './index.css';
import { CurrentProfit } from '../../../../../ProfitCalculations/types';

interface EnhancedProps {
  index: number;
  transactions: Transaction[];
  compactTransactionIndex: number;
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: Transaction[][];
  profitCalcsCurrentProfit: CurrentProfit | undefined;
}

interface Props {
  index: number;
  transactions: Transaction[];
  compactTransactionIndex: number;
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: Transaction[][];
  toggleCompactTransaction?: () => void;
  isCompact?: boolean;
  isVeryFirst?: boolean;
  isFirstClusteredTransaction?: boolean;
  clusteredTransactionIndex?: number;
  totalClusteredTransactions?: number;
  profitCalcsCurrentProfit: CurrentProfit | undefined;
}

const TransactionItem: React.FC<Props> = (props: Props) => {
  const {
    index,
    transactions,
    compactTransactionIndex,
    currentProfit,
    currentPriceChange,
    toggleCompactTransaction,
    isCompact = true,
    isVeryFirst = false,
    isFirstClusteredTransaction = false,
    totalClusteredTransactions = 0,
    clusteredTransactionIndex = 0,
    profitCalcsCurrentProfit,
  } = props;

  const leftTransaction = transactions[0];
  const rightTransaction = transactions[1];

  const transactionOrderColor = useCallback(
    (orderType: 'BUY' | 'SELL') =>
      orderType === 'BUY' ? colors.green : colors.red,
    [],
  );

  const transactionSuccessColor = useCallback(
    (success: boolean | string | null) =>
      success === null
        ? colors.white
        : typeof success === 'string'
        ? colors.orange
        : success
        ? colors.green
        : colors.red,
    [],
  );

  const getProfitValue = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return profit;
    }

    if (profit === null) {
      return formatPercentage(0);
    }

    if (typeof profit === 'string') {
      return formatPercentage(parseFloat(profit));
    }

    return formatPercentage(profit);
  }, []);

  const getProfitColor = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return colors.orange;
    }

    if (profit === null) {
      return colors.white;
    }

    if (typeof profit === 'string') {
      return parseFloat(profit) >= 0 ? colors.green : colors.red;
    }

    return profit >= 0 ? colors.green : colors.red;
  }, []);

  const multiOrderIndex = useMemo(() => {
    if (isCompact) {
      return 'A';
    }

    return totalClusteredTransactions - clusteredTransactionIndex;
  }, [clusteredTransactionIndex, isCompact, totalClusteredTransactions]);

  return (
    <div
      className="detail-double-transactions-item-container"
      style={index === 0 && (isVeryFirst || isCompact) ? { marginTop: 0 } : {}}
    >
      <div className="detail-double-transactions-item-index-container">
        {(!!index || (!isVeryFirst && !isCompact)) && (
          <div className="detail-double-transactions-item-index-divider"></div>
        )}
        <div
          className="detail-double-transactions-item-index-circle"
          style={{
            borderColor: colors.green,
          }}
        >
          <span className="detail-double-transactions-item-index-text">
            {leftTransaction.index ? parseInt(leftTransaction.index, 10) : 'x'}
            <div
              className="detail-double-transactions-item-multi-index-circle"
              style={{
                borderColor: transactionOrderColor(leftTransaction.orderType),
              }}
            >
              <span className="detail-double-transactions-item-multi-index-text">
                {multiOrderIndex}
              </span>
            </div>
          </span>
        </div>

        {index === 0 ? (
          <div className="detail-double-transactions-item-index-divider"></div>
        ) : (
          <div className="detail-double-transactions-item-index-divider-small"></div>
        )}
      </div>

      <div className="detail-double-transactions-item-info-container-connector"></div>

      <div
        className="detail-double-transactions-item-info-container"
        style={
          index === 0 && (isVeryFirst || isCompact) ? { marginTop: 50 } : {}
        }
      >
        <div className="detail-double-transactions-item-info-container-date-container">
          <span className="detail-double-transactions-item-info-container-date-text">
            {moment(leftTransaction.transactionTimestamp).format(
              'D MMM YYYY HH:mm',
            )}
          </span>
        </div>

        {profitCalcsCurrentProfit &&
          compactTransactionIndex === 0 &&
          (isVeryFirst || isCompact) && (
            <div className="detail-double-transactions-item-info-container-header-container">
              <span className="detail-double-transactions-item-info-container-header-label">
                Current Profit:
              </span>
              <span
                className="detail-double-transactions-item-info-container-header-value"
                style={{
                  color: getProfitColor(currentProfit || 0),
                }}
              >
                {formatPercentage(currentProfit || 0)}
              </span>
              <span className="detail-double-transactions-item-info-container-header-label detail-double-transactions-item-info-container-header-left">
                Current Price Change:
              </span>
              <span
                className="detail-double-transactions-item-info-container-header-value"
                style={{
                  color: getProfitColor(currentProfit || 0),
                }}
              >
                {formatPercentage(currentPriceChange || 0)}
              </span>
            </div>
          )}

        <div className="detail-double-transactions-item-info-container-row">
          <div className="detail-double-transactions-item-info-container-column">
            {/** Left */}
            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label detail-double-transactions-item-info-container-value-bigger">
                {isCompact ? 'Average Price:' : 'Price:'}
              </span>
              <span className="detail-double-transactions-item-info-container-value-text detail-double-transactions-item-info-container-value-bigger">
                {isCompact
                  ? formatPrice(leftTransaction.averageOrderPrice ?? 0)
                  : formatPrice(leftTransaction.orderPrice ?? 0)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Asset quantity:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(leftTransaction.assetQuantity, false)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container container-spacer">
              <span className="detail-double-transactions-item-info-container-value-label">
                Asset Profit:
              </span>
              <span
                className="detail-double-transactions-item-info-container-value-text"
                style={{
                  color:
                    leftTransaction.orderType === 'BUY'
                      ? getProfitColor(leftTransaction.profit)
                      : colors.white,
                }}
              >
                {leftTransaction.orderType === 'BUY'
                  ? getProfitValue(leftTransaction.profit)
                  : '0%'}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Asset Balance:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(leftTransaction.currentAssetBalance, false)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Total Fee:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(leftTransaction.totalFee)}
              </span>
            </div>
          </div>
          <div className="detail-double-transactions-item-info-container-column middle-container detail-double-transactions-item-info-container-middle-column">
            {/** Middle */}
            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Stable quantity:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(leftTransaction.stableQuantity)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container container-spacer">
              <span className="detail-double-transactions-item-info-container-value-label">
                Stable Profit:
              </span>
              <span
                className="detail-double-transactions-item-info-container-value-text"
                style={{
                  color:
                    leftTransaction.orderType === 'SELL'
                      ? getProfitColor(leftTransaction.profit)
                      : colors.white,
                }}
              >
                {leftTransaction.orderType === 'SELL'
                  ? getProfitValue(leftTransaction.profit)
                  : '0%'}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Stable Balance:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(leftTransaction.currentStableBalance)}
              </span>
            </div>
            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Fee Value:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(leftTransaction.currentFee)}
              </span>
            </div>
          </div>
        </div>

        {(isFirstClusteredTransaction || isCompact) && (
          <div className="detail-double-transactions-item-info-container-see-all-container">
            <div className="detail-double-transactions-item-info-container-see-all-container-connector"></div>
            <span
              className="detail-double-transactions-item-info-container-see-all-container-text"
              onClick={toggleCompactTransaction}
            >
              {isCompact
                ? 'See all specific transactions'
                : 'See compact transaction'}
            </span>
          </div>
        )}
      </div>

      <div className="detail-double-transactions-item-connector"></div>

      <div
        className="detail-double-transactions-item-info-container"
        style={{ marginLeft: 0 }}
      >
        {compactTransactionIndex === 0 && (isVeryFirst || isCompact) && (
          <div className="detail-double-transactions-item-info-container-header-container">
            <span className="detail-double-transactions-item-info-container-header-label">
              Current Profit:
            </span>
            <span
              className="detail-double-transactions-item-info-container-header-value"
              style={{
                color: getProfitColor(
                  profitCalcsCurrentProfit?.currentProfit || currentProfit || 0,
                ),
              }}
            >
              {formatPercentage(
                profitCalcsCurrentProfit?.currentProfit || currentProfit || 0,
              )}
            </span>
            <span className="detail-double-transactions-item-info-container-header-label detail-double-transactions-item-info-container-header-left">
              Current Price Change:
            </span>
            <span
              className="detail-double-transactions-item-info-container-header-value"
              style={{
                color: getProfitColor(
                  profitCalcsCurrentProfit?.currentProfit || currentProfit || 0,
                ),
              }}
            >
              {formatPercentage(
                profitCalcsCurrentProfit?.currentPriceChange ||
                  currentPriceChange ||
                  0,
              )}
            </span>
          </div>
        )}

        <div className="detail-double-transactions-item-info-container-row">
          <div className="detail-double-transactions-item-info-container-column">
            {/** Left */}
            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label detail-double-transactions-item-info-container-value-bigger">
                {isCompact ? 'Average Price:' : 'Price:'}
              </span>
              <span className="detail-double-transactions-item-info-container-value-text detail-double-transactions-item-info-container-value-bigger">
                {isCompact
                  ? formatPrice(rightTransaction.averageOrderPrice ?? 0)
                  : formatPrice(rightTransaction.orderPrice ?? 0)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Asset quantity:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(rightTransaction.assetQuantity, false)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container container-spacer">
              <span className="detail-double-transactions-item-info-container-value-label">
                Asset Profit:
              </span>
              <span
                className="detail-double-transactions-item-info-container-value-text"
                style={{
                  color:
                    rightTransaction.orderType === 'BUY'
                      ? getProfitColor(rightTransaction.profit)
                      : colors.white,
                }}
              >
                {rightTransaction.orderType === 'BUY'
                  ? getProfitValue(rightTransaction.profit)
                  : '0%'}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Asset Balance:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(rightTransaction.currentAssetBalance, false)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Total Fee:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(rightTransaction.totalFee)}
              </span>
            </div>
          </div>
          <div className="detail-double-transactions-item-info-container-column middle-container detail-double-transactions-item-info-container-middle-column">
            {/** Middle */}
            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Stable quantity:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(rightTransaction.stableQuantity)}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container container-spacer">
              <span className="detail-double-transactions-item-info-container-value-label">
                Stable Profit:
              </span>
              <span
                className="detail-double-transactions-item-info-container-value-text"
                style={{
                  color:
                    rightTransaction.orderType === 'SELL'
                      ? getProfitColor(rightTransaction.profit)
                      : colors.white,
                }}
              >
                {rightTransaction.orderType === 'SELL'
                  ? getProfitValue(rightTransaction.profit)
                  : '0%'}
              </span>
            </div>

            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Stable Balance:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(rightTransaction.currentStableBalance)}
              </span>
            </div>
            <div className="detail-double-transactions-item-info-container-value-container">
              <span className="detail-double-transactions-item-info-container-value-label">
                Fee Value:
              </span>
              <span className="detail-double-transactions-item-info-container-value-text">
                {formatBalance(rightTransaction.currentFee)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EnhancedTransactionItem: React.FC<EnhancedProps> = (
  props: EnhancedProps,
): any => {
  const { clusteredTransactions, compactTransactionIndex } = props;
  const [showCompactTransaction, setShowCompactTransaction] = useState(true);

  const toggleCompactTransaction = useCallback(() => {
    setShowCompactTransaction((prev) => !prev);
  }, []);

  if (showCompactTransaction) {
    return (
      <TransactionItem
        {...props}
        toggleCompactTransaction={toggleCompactTransaction}
      />
    );
  }

  if (clusteredTransactions.length) {
    return clusteredTransactions.map((t, index) => {
      return (
        <TransactionItem
          key={t[0].id}
          {...props}
          transactions={t}
          isCompact={false}
          isVeryFirst={compactTransactionIndex === 0 && index === 0}
          toggleCompactTransaction={toggleCompactTransaction}
          isFirstClusteredTransaction={index === 0}
          clusteredTransactionIndex={index}
          totalClusteredTransactions={clusteredTransactions.length}
        />
      );
    });
  }

  return (
    <TransactionItem
      {...props}
      toggleCompactTransaction={toggleCompactTransaction}
    />
  );
};

export default EnhancedTransactionItem;
